import { useEffect, useState } from 'react';

import { faArrowLeft, faArrowRight, faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import { toast as sonnerToast } from 'sonner';

import { StepIndicator } from './FormStepIndicator';
import { GenerateDetailsButton } from './GenerateDetailsButton';
import { StartProcessingCase } from './StartProcessingCase';
import { useGenerateCaseDetails } from '@/api/mutations/useGenerateCaseDetails';
import { useGetCaseIsPendingUpload } from '@/api/queries/useGetCaseIsPendingUpload';
import useGetFiles from '@/api/queries/useGetFiles';
import { FullCaseForm } from '@/api/schemas/case';
import { CaseCreationRequestDetails } from '@/api/types/mutations';
import { BackButton } from '@/components/atoms/BackButton';
import Button from '@/components/atoms/Button';
import { Input } from '@/components/atoms/input';
import { Skeleton } from '@/components/atoms/Skeleton';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/molecules/form';
import ResizingTextarea from '@/components/molecules/ResizingTextarea';
import { DISPUTE_STATUS_OPTIONS } from '@/constants/disputeStatusOptions';
import { reactSelectStylesCaseCreator } from '@/constants/styles';
import { ActiveJob } from '@/hooks/useJobProgress';
import { UploadMode } from '@/types';

const RequiredInfo = ({
  form,
  mode,
  isGeneratingDetails,
  onGenerateDetails,
}: {
  form: UseFormReturn<FullCaseForm>;
  mode: UploadMode;
  isGeneratingDetails: boolean;
  onGenerateDetails: (file: File) => void;
}) => {
  return (
    <>
      <div className="-mb-1 flex w-full items-center justify-between">
        <p className="text-sm text-gray-600">Fill in some key details about the case.</p>
        <GenerateDetailsButton isGeneratingDetails={isGeneratingDetails} onGenerateDetails={onGenerateDetails} />
      </div>

      <div className="flex flex-col gap-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Case Name</FormLabel>
              <FormControl className="mt-1">
                <div className="relative">
                  {isGeneratingDetails ? <Skeleton className="absolute inset-3 z-10 h-4 w-2/5 bg-gray-100" /> : null}
                  <Input
                    placeholder={isGeneratingDetails ? '' : 'Enter the name of the case'}
                    data-testid="case-name-input"
                    className={isGeneratingDetails ? 'text-transparent' : ''}
                    {...field}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="legalIssues"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Case Issues</FormLabel>
              <FormControl className="mt-1">
                <div className="relative">
                  {isGeneratingDetails ? (
                    <>
                      <Skeleton className="absolute top-3 left-3 z-10 h-4 w-3/5 bg-gray-100" />
                      <Skeleton className="absolute top-8 left-3 z-10 h-4 w-4/5 bg-gray-100" />
                      <Skeleton className="absolute top-[52px] left-3 z-10 h-4 w-2/5 bg-gray-100" />
                    </>
                  ) : null}
                  <ResizingTextarea
                    className={`text-sm ${isGeneratingDetails ? 'text-transparent' : ''}`}
                    data-testid="case-issues-input"
                    placeholder={
                      isGeneratingDetails
                        ? ''
                        : 'Outline the key issues you want to explore, either in list format or prose.'
                    }
                    minRows={5}
                    maxRows={10}
                    {...field}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {mode === UploadMode.Full && (
          <>
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Case Type</FormLabel>
                  <FormControl className="mt-1">
                    <div className="relative">
                      {isGeneratingDetails ? (
                        <Skeleton className="absolute inset-3 z-10 h-4 w-3/5 bg-gray-100" />
                      ) : null}
                      <Input
                        data-testid="case-type-input"
                        className={isGeneratingDetails ? 'text-transparent' : ''}
                        placeholder={
                          isGeneratingDetails
                            ? ''
                            : 'Arbitration, Employment Dispute, Public Inquiry, Commercial Litigation, etc...'
                        }
                        {...field}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="parties"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Case Entities</FormLabel>
                  <FormControl className="mt-1">
                    <div className="relative">
                      {isGeneratingDetails ? (
                        <Skeleton className="absolute inset-3 z-10 h-4 w-2/5 bg-gray-100" />
                      ) : null}
                      <Input
                        data-testid="case-entities-input"
                        className={isGeneratingDetails ? 'text-transparent' : ''}
                        placeholder={isGeneratingDetails ? '' : 'Key entities or entities in the case.'}
                        {...field}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}

        {/* <FormField
        control={form.control}
        name="billable"
        render={({ field }) => (
          <FormItem>
            <div className="flex flex-row items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <FormLabel>Billable Case</FormLabel>
                <div className="text-xs text-gray-600">Mark this case as billable for usage tracking and reporting</div>
              </div>
              <FormControl className="mt-1">
                <Switch
                  className="bg-gray-100"
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                  data-testid="case-billable-toggle"
                />
              </FormControl>
            </div>
            <FormMessage />
          </FormItem>
        )}
      /> */}
      </div>
    </>
  );
};

const OptionalInfo = ({ form }: { form: UseFormReturn<FullCaseForm> }) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      <FormField
        control={form.control}
        name="keyContext"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Additional Case Context (Optional)</FormLabel>
            <FormControl className="mt-1">
              <ResizingTextarea
                className="text-sm"
                placeholder={`Any other contextual information Wexler should be aware of. E.g. "There is a parallel litigation happening in a different country with similar issues" or "The ${t('claimant')} is a public figure".`}
                minRows={3}
                maxRows={10}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="keyTimePeriod"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Case Time Period (Optional)</FormLabel>
            <FormControl className="mt-1">
              <ResizingTextarea
                className="text-sm"
                data-testid="case-time-period-input"
                placeholder={
                  'The estimated time period of the documents, e.g. "1960 to 1990" or "Early 2000s" (don\'t worry about the formatting of the dates).'
                }
                minRows={2}
                maxRows={5}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="disputeStatus"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Dispute Status (Optional)</FormLabel>
            <FormControl className="mt-1">
              <Select
                options={Object.values(DISPUTE_STATUS_OPTIONS)}
                className={`w-full rounded-md text-sm outline-none`}
                styles={reactSelectStylesCaseCreator}
                onChange={(newValue) => field.onChange(newValue?.value)}
                value={DISPUTE_STATUS_OPTIONS[field.value as keyof typeof DISPUTE_STATUS_OPTIONS]}
                placeholder={'Pre-Action, Claim, Trial, Post-Trial'}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

interface CaseDetailsProps {
  form: UseFormReturn<FullCaseForm>;
  mode: UploadMode;
  onSubmit: (data: Omit<CaseCreationRequestDetails, 'caseId'>) => void;
  uploadDisabled: boolean;
  handleClickBack?: () => void;
  isProcessingCase: boolean;
  activeJobs?: ActiveJob[];
}

const CaseDetails = ({
  form,
  mode,
  onSubmit,
  uploadDisabled,
  handleClickBack,
  isProcessingCase,
  activeJobs,
}: CaseDetailsProps) => {
  const [step, setStep] = useState(0);
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId') ?? '';
  const { mutate: generateCaseDetails, isLoading: isGeneratingDetails } = useGenerateCaseDetails();

  // Check if required form fields are valid
  const isFormValid = !!form.watch('name')?.trim() && !!form.watch('legalIssues')?.trim();

  const handleGenerateDetails = (file: File) => {
    generateCaseDetails(
      { file },
      {
        onSuccess: (data) => {
          // if succeeded but all fields are null, show error
          if (Object.values(data).every((value) => value === null)) {
            sonnerToast.error(
              `Couldn't extract any case details from the document. Please try again with a different file.`,
            );

            return;
          }

          if (data.caseName) form.setValue('name', data.caseName);
          if (data.caseIssues) form.setValue('legalIssues', data.caseIssues);
          if (data.caseType) form.setValue('type', data.caseType);
          if (data.caseEntities) form.setValue('parties', data.caseEntities);

          sonnerToast.success('Case details extracted successfully!');
        },
        onError: () => {
          sonnerToast.error('Failed to extract case details. Please try again.');
        },
      },
    );
  };

  // This function checks whether there is an ongoing upload from the user.
  const { data: isPendingUploadData, refetch: isPendingUploadRefetch } = useGetCaseIsPendingUpload(caseId);
  const isPendingUpload = async (): Promise<boolean> => {
    const { data } = await isPendingUploadRefetch();
    return data === true;
  };


  // Processes cases only where an ongoing upload from the user is not present.
  const processCaseWrapper = async () => {
    const isUploadOngoing = await isPendingUpload();
    if (isUploadOngoing) {
      sonnerToast.info('An upload is still in progress. Please wait and try again later.', {
        duration: 3000,
        position: 'top-right',
        richColors: true,
      });
    } else {
      form.handleSubmit(onSubmit)();
    }
  };

  // Messy, but a quick fix until Redis is implemented.
  const { refetch: refetchFiles } = useGetFiles(caseId, 0);
  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (isPendingUploadData === true) {
      interval = setInterval(() => {
        refetchFiles();
        isPendingUploadRefetch();
      }, 10_000);
    } else if (isPendingUploadData !== true) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isPendingUploadData, refetchFiles, isPendingUploadRefetch]);

  const handleNextStep = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      setStep(1);
    }
  };

  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-2 border-b border-gray-100 pb-4">
        <div className="flex items-center">
          {handleClickBack && (
            <div className="mr-4 flex items-center">
              <BackButton onClick={handleClickBack} />
            </div>
          )}
          <FontAwesomeIcon icon={faPenToSquare} className="mr-3 h-4 w-4 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Details</h2>
        </div>
        <StepIndicator step={step} />
      </div>

      <div className="mt-5 flex flex-col gap-6">
        {step === 0 && (
          <RequiredInfo
            form={form}
            mode={mode}
            isGeneratingDetails={isGeneratingDetails}
            onGenerateDetails={handleGenerateDetails}
          />
        )}
        {step === 1 && <OptionalInfo form={form} />}
        <div className="flex justify-between">
          {step === 1 && (
            <Button
              type="secondary"
              onClick={() => setStep(0)}
              text="Back"
              rounded="md"
              icon={<FontAwesomeIcon icon={faArrowLeft} className="mr-2 h-4 w-4" />}
            />
          )}
          {/*If multi step then show next button, else show start processing button*/}
          {step === 1 || mode === UploadMode.Light ? (
            <StartProcessingCase
              uploadDisabled={uploadDisabled}
              handleUpdateCase={async () => await processCaseWrapper()}
              isLoading={isProcessingCase}
              activeJobs={activeJobs}
              isFormValid={isFormValid}
            />
          ) : (
            <div className="flex w-full">
              <Button
                testid="next-step-btn"
                onClick={handleNextStep}
                text="Next"
                type="primary"
                rounded="md"
                icon={<FontAwesomeIcon icon={faArrowRight} className="ml-2 h-4 w-4" />}
                iconRight
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CaseDetails;
